import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import { Provider as JotaiProvider } from 'jotai';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { initI18n } from '@/i18n';
import { AuthProvider } from '@/providers/AuthProvider';
import { router } from '@/routes';
import { initStandaloneMode, isStandaloneMode } from '@/utils/standalone';

import '@shopify/polaris/build/esm/styles.css';

// A missing #root means index.html was rewritten or the bundle was injected
// somewhere unexpected. `!` would turn that into "Cannot read properties of
// null", which says nothing about the cause.
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Confetto: #root is missing from the document.');

/**
 * ⚠ BEFORE anything else, and before React.
 *
 * `initStandaloneMode()` installs the mock `window.shopify` that every
 * `shopify.*` call in the app resolves against. A component that mounts first
 * would read `undefined` and throw — which is exactly what "Cannot read
 * properties of undefined (reading 'idToken')" is.
 */
if (isStandaloneMode()) initStandaloneMode();

/**
 * ⚠ The language is loaded BEFORE the first render, not alongside it.
 *
 * Rendering first and swapping the dictionary in afterwards means a German
 * merchant sees a flash of English on every boot — and Polaris's own chrome
 * would arrive in a different language than the app's for that frame. One await
 * of one small chunk is cheaper than the flicker.
 */
const { locale, polaris } = await initI18n();
document.documentElement.lang = locale;

createRoot(rootEl).render(
  <StrictMode>
    <JotaiProvider>
      {/* ⚠ INSIDE AppProvider, outside AuthProvider. The boundary renders
          Polaris components, so it needs Polaris's context — placed above
          AppProvider it would throw while trying to display the throw. Below
          AuthProvider it would miss the boot failure, which is the most likely
          thing to break. */}
      <AppProvider i18n={polaris}>
        <ErrorBoundary>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </ErrorBoundary>
      </AppProvider>
    </JotaiProvider>
  </StrictMode>
);
